/**
 * Custom hook to handle mode-specific employee search operations.
 * Extracts search dispatch logic to reduce cognitive complexity.
 */
import { useAppDispatch } from '../../../../store/hooks'
import type { ProxyMode } from '../../../../components/common/config/adminConfig'
import type { TrimmedEmployeeSearch } from './ProxyEditPanelUtils'
import { searchAvailableEmployees } from '../../../../store/slices/admin/approverProxySlice'
import { searchAvailableEmployeesRP } from '../../../../store/slices/admin/requestorProxySlice'
import { searchAvailableFloatEmployees } from '../../../../store/slices/admin/floatEmployeeSlice'

/**
 * Custom hook providing mode-specific employee search dispatch.
 * @param mode - Current proxy mode ('approver', 'float', or 'requestor')
 * @param approvalGrpId - Approval group ID for the search
 * @returns Search handler function
 */
export const useEmployeeSearch = (mode: ProxyMode, approvalGrpId: string) => {
  const dispatch = useAppDispatch()

  /**
   * Dispatches the available-employee search with contract-shaped query params.
   * - Approver mode: AP endpoint (`empAPSearch`)
   * - Float mode: FE endpoint (`empFESearch`)
   * - Requestor mode: RP endpoint (`empRPSearch`)
   *
   * @param trimmed - Trimmed field values.
   * @param searchType - 'name' or 'nuid'
   */
  const dispatchEmployeeSearch = (
    trimmed: TrimmedEmployeeSearch,
    searchType: string
  ) => {
    const payload =
      searchType === 'name'
        ? {
            approvalGrpId,
            searchOption: 'name' as const,
            searchLName: trimmed.lastName,
            searchFName: trimmed.firstName || undefined,
          }
        : {
            approvalGrpId,
            searchOption: 'nuid' as const,
            searchValue: trimmed.nuid,
          }

    if (mode === 'approver') {
      dispatch(searchAvailableEmployees(payload))
    } else if (mode === 'float') {
      dispatch(searchAvailableFloatEmployees(payload))
    } else {
      dispatch(searchAvailableEmployeesRP(payload))
    }
  }

  return { dispatchEmployeeSearch }
}
